import { lookupIP } from "@/services/general-service";
import { useEffect, useState } from "react";

interface UserLocation {
  lat: number | null;
  lng: number | null;
  city: string;
  region: string;
  country: string;
  loading: boolean;
  error: string | null;
}

// Get user's public IP using WebRTC (no external API needed)
const getUserIP = async (): Promise<string | null> => {
  return new Promise((resolve) => {
    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      pc.createDataChannel("");
      pc.createOffer()
        .then((offer) => pc.setLocalDescription(offer))
        .catch(() => resolve(null));

      pc.onicecandidate = (ice) => {
        if (!ice || !ice.candidate || !ice.candidate.candidate) {
          return;
        }

        const ipRegex = /([0-9]{1,3}\.){3}[0-9]{1,3}/;
        const match = ipRegex.exec(ice.candidate.candidate);

        if (match) {
          pc.close();
          resolve(match[0]);
        }
      };

      // Timeout after 5 seconds
      setTimeout(() => {
        pc.close();
        resolve(null);
      }, 5000);
    } catch (err) {
      resolve(null);
    }
  });
};

// Fallback: Get IP from a lightweight service (only if WebRTC fails)
const getFallbackIP = async (): Promise<string | null> => {
  try {
    // Using cloudflare's trace endpoint (more reliable than ipapi)
    const response = await fetch("https://www.cloudflare.com/cdn-cgi/trace");
    const data = await response.text();
    const ipMatch = data.match(/ip=([^\n]+)/);
    return ipMatch ? ipMatch[1] : null;
  } catch {
    return null;
  }
};

export const useUserLocation = (): UserLocation => {
  const [location, setLocation] = useState<UserLocation>({
    lat: null,
    lng: null,
    city: "",
    region: "",
    country: "",
    loading: true,
    error: null,
  });

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        // Step 1: Try to get IP using WebRTC (no external API call)
        let ip = await getUserIP();

        // Step 2: If WebRTC fails, use fallback (Cloudflare trace - more reliable)
        if (!ip) {
          ip = await getFallbackIP();
        }

        if (!ip) {
          setLocation((prev) => ({
            ...prev,
            loading: false,
            error: "Unable to determine IP address",
          }));
          return;
        }

        // Step 3: Call your custom backend lookup
        const lookupRes = await lookupIP(ip);

        if (lookupRes.success && lookupRes.data) {
          const d = lookupRes.data;
          setLocation({
            lat: d.latitude || null,
            lng: d.longitude || null,
            city: d.city || "",
            region: d.region || "",
            country: d.countryName || "",
            loading: false,
            error: null,
          });
        } else {
          throw new Error("IP lookup failed");
        }
      } catch (err: any) {
        setLocation((prev) => ({
          ...prev,
          loading: false,
          error: err.message || "Unable to determine location",
        }));
      }
    };

    fetchLocation();
  }, []);

  return location;
};

// OLD

// import { lookupIP } from "@/services/general-service";
// import { useEffect, useState } from "react";

// interface UserLocation {
//   lat: number | null;
//   lng: number | null;
//   city: string;
//   region: string;
//   country: string;
//   loading: boolean;
//   error: string | null;
// }

// export const useUserLocation = (): UserLocation => {
//   const [location, setLocation] = useState<UserLocation>({
//     lat: null,
//     lng: null,
//     city: "",
//     region: "",
//     country: "",
//     loading: true,
//     error: null,
//   });

//   useEffect(() => {
//     const fetchLocation = async () => {
//       try {
//         // Step 1: Get user's public IP from ipapi
//         const ipRes = await fetch(`https://ipapi.co/ip/`);
//         const ip = (await ipRes.text()).trim();

//         // console.log(ip);

//         if (!ip) {
//           // throw new Error("Unable to fetch IP address");
//           // console.log("Unable to fetch IP address");
//           return;
//         }

//         // Step 2: Call your custom backend lookup
//         const lookupRes = await lookupIP(ip);

//         // console.log(lookupRes);

//         if (lookupRes.success && lookupRes.data) {
//           const d = lookupRes.data;
//           setLocation({
//             lat: d.latitude || null,
//             lng: d.longitude || null,
//             city: d.city || "",
//             region: d.region || "",
//             country: d.countryName || "",
//             loading: false,
//             error: null,
//           });
//         } else {
//           throw new Error("IP lookup failed");
//         }
//       } catch (err: any) {
//         setLocation((prev) => ({
//           ...prev,
//           loading: false,
//           error: err.message || "Unable to determine location",
//         }));
//       }
//     };

//     fetchLocation();
//   }, []);

//   return location;
// };
